import React, { useEffect, useState } from 'react';
import { paidContentRestricted } from '../lib/platform';
import './QuotaModal.css';

// Pretty-print "X hours Y min" for the countdown to the next reset.
// Sub-minute remainders read as "less than a minute" to avoid a jumpy
// last-second second-counter.
function formatRemaining(ms) {
  if (ms <= 0) return 'any moment now';
  const totalMin = Math.ceil(ms / 60000);
  if (totalMin < 1) return 'less than a minute';
  if (totalMin < 60) return `${totalMin} minute${totalMin === 1 ? '' : 's'}`;
  const totalH = Math.ceil(totalMin / 60);
  if (totalH < 24) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
    return `${h}h ${m}m`;
  }
  const d = Math.floor(totalH / 24);
  const h = totalH % 24;
  if (h === 0) return `${d} day${d === 1 ? '' : 's'}`;
  return `${d}d ${h}h`;
}

function formatIstReset(resetMs) {
  if (!resetMs) return '12:00 AM IST';
  try {
    return new Date(resetMs).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' IST';
  } catch {
    return '12:00 AM IST';
  }
}

// Lookup for each `kind` returned by the server. Keeps copy in one
// place so the daily/monthly + tokens/images axes don't fan out into
// four near-identical bodies.
const COPY = {
  DAILY_TOKEN_LIMIT: {
    title: 'Daily message limit reached',
    metric: 'tokens',
    window: 'day',
    detail: (lim) => <>You've used all <strong>{fmt(lim)} tokens</strong> for today.</>,
  },
  MONTHLY_TOKEN_LIMIT: {
    title: 'Monthly message limit reached',
    metric: 'tokens',
    window: 'month',
    detail: (lim) => <>You've used all <strong>{fmt(lim)} tokens</strong> for this month.</>,
  },
  DAILY_IMAGE_LIMIT: {
    title: 'Daily image limit reached',
    metric: 'images',
    window: 'day',
    detail: (lim) => <>You've generated all <strong>{lim} images</strong> for today.</>,
  },
  MONTHLY_IMAGE_LIMIT: {
    title: 'Monthly image limit reached',
    metric: 'images',
    window: 'month',
    detail: (lim) => <>You've generated all <strong>{lim} images</strong> for this month.</>,
  },
};

function fmt(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

// Modal that pops when the server returns 429 QUOTA_EXCEEDED. Shows
// daily-or-monthly + tokens-or-images context, and offers a Subscribe
// CTA for free users.
//
//   kind     — server's err.kind: DAILY_TOKEN_LIMIT | MONTHLY_TOKEN_LIMIT
//              | DAILY_IMAGE_LIMIT | MONTHLY_IMAGE_LIMIT
//   usage    — { plan, limits, dailyResetMs, monthlyResetMs, ... }
//   onClose
//   onSubscribe — Upgrade button handler from the parent
export default function QuotaModal({ kind, usage, onClose, onSubscribe }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const meta = COPY[kind] || COPY.DAILY_TOKEN_LIMIT;
  const limits = usage?.limits || {};
  const planLimit = limits[
    meta.metric === 'tokens'
      ? (meta.window === 'day' ? 'tokensDay' : 'tokensMonth')
      : (meta.window === 'day' ? 'imagesDay' : 'imagesMonth')
  ];
  const resetMs = meta.window === 'day' ? usage?.dailyResetMs : usage?.monthlyResetMs;
  const remainingMs = resetMs ? resetMs - Date.now() : 0;
  const isFree = usage?.plan !== 'pro';

  return (
    <div
      className="quota-modal-bg"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="quota-modal-card" role="dialog" aria-modal="true" aria-labelledby="quota-modal-title">
        <button className="quota-modal-x" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="quota-modal-icon" aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 22h14M5 2h14M6 22v-4.172A2 2 0 0 1 6.586 16.414L10 13l-3.414-3.414A2 2 0 0 1 6 8.172V4M18 22v-4.172a2 2 0 0 0-.586-1.414L14 13l3.414-3.414A2 2 0 0 0 18 8.172V4" />
          </svg>
        </div>

        <h2 id="quota-modal-title" className="quota-modal-title">
          {meta.title}
        </h2>

        <p className="quota-modal-body">
          {meta.detail(planLimit || 0)}
          {' '}Limit resets at <strong>{formatIstReset(resetMs)}</strong>
          {remainingMs > 0 ? <> — in about <strong>{formatRemaining(remainingMs)}</strong>.</> : '.'}
        </p>

        <div className="quota-modal-meter" aria-hidden="true">
          <div className="quota-modal-meter-fill" />
        </div>
        <div className="quota-modal-meter-label">
          {meta.metric === 'tokens'
            ? `${fmt(planLimit || 0)} / ${fmt(planLimit || 0)} tokens used`
            : `${planLimit || 0} / ${planLimit || 0} images generated`}
        </div>

        {/* iOS builds must not reference paid content while the
            matching IAP isn't submitted — Apple rejects the binary
            otherwise. Hide the Upgrade CTA there; show a neutral
            close button instead. Web + Android keep the upsell. */}
        {isFree && !paidContentRestricted() ? (
          <>
            <button className="quota-modal-btn-upgrade" onClick={() => { onSubscribe?.(); }}>
              Upgrade to Pro · $0.7/day
              <span className="quota-modal-btn-sub">10× limits, billed monthly</span>
            </button>
            <button className="quota-modal-btn-secondary" onClick={onClose}>
              I'll wait
            </button>
          </>
        ) : (
          <button className="quota-modal-btn" onClick={onClose}>
            Got it
          </button>
        )}
      </div>
    </div>
  );
}
